/**
 * A cloud production's status (PLAN §9.2) on the broadcast palette (D25):
 * `running` is on air, `starting` is next up, `pending` (created, or waiting
 * for a scheduled launch) is previewed, `stopping` warns, `stopped` is
 * offline and `failed` is destructive. `unknown` — a status a newer cloud
 * reports that this build does not know (an open enum, D37) — is neutral.
 */
import type { CloudProductionStatusOpenWire } from '@acp/api-contract/cloudProductions.js';
import { UNKNOWN_HINT, UNKNOWN_LABEL } from '../../lib/openEnum.js';
import { Badge, type BadgeProps } from '../ui/badge.js';

const LOOK: Readonly<Record<CloudProductionStatusOpenWire, { readonly label: string; readonly variant: NonNullable<BadgeProps['variant']> }>> = {
  pending: { label: 'Pending', variant: 'preview' },
  starting: { label: 'Starting', variant: 'next' },
  running: { label: 'Running', variant: 'onair' },
  stopping: { label: 'Stopping', variant: 'warning' },
  stopped: { label: 'Stopped', variant: 'offline' },
  failed: { label: 'Failed', variant: 'destructive' },
  unknown: { label: UNKNOWN_LABEL, variant: 'secondary' },
};

export function CloudProductionStatusBadge({ status }: { readonly status: CloudProductionStatusOpenWire }) {
  const look = LOOK[status];
  return (
    <Badge variant={look.variant} data-testid="cloud-status" data-status={status} title={status === 'unknown' ? UNKNOWN_HINT : undefined}>
      {look.label}
    </Badge>
  );
}
